// SPDX-FileCopyrightText: 2026 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get, useAuthContext } from '@tls/auth';
import { parseBanner, type BannerDto } from 'model/Banner';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { checkHostedUiVersion, refreshUiForMaintenanceStart } from 'utilities/uiVersion';
import { getAppStatusQueryKey, type AppStatus } from './useAppStatusData';

const POLL_INTERVAL = 5 * 60 * 1000; //every 5 minutes
const RECOVERY_POLL_INTERVAL = 15 * 1000;

export function useAppStatusApi() {
  const { appBaseUrl } = useAuthContext();
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'en';
  const [recoveryActive, setRecoveryActive] = useState(false);
  const hasSuccessfulVersionHeartbeat = useRef(false);
  const refetchInterval = recoveryActive ? RECOVERY_POLL_INTERVAL : POLL_INTERVAL;

  useEffect(() => {
    // Nav still observes the cache, so leaving the controller must cancel the request explicitly.
    return () => {
      void queryClient.cancelQueries({ queryKey: getAppStatusQueryKey(appBaseUrl, lang), exact: true });
    };
  }, [queryClient, appBaseUrl, lang]);

  return useQuery<AppStatus>({
    queryKey: getAppStatusQueryKey(appBaseUrl, lang),
    queryFn: async ({ signal }) => {
      const [banner, versionStatus] = await Promise.all([
        getBanner(lang),
        checkHostedUiVersion({ forceReload: recoveryActive, appBaseUrl, signal }).catch(error => {
          console.warn('UI version check failed', error);
          return 'unreachable' as const;
        }),
      ]);
      signal.throwIfAborted();

      const maintenanceStarted =
        !recoveryActive && hasSuccessfulVersionHeartbeat.current && versionStatus === 'temporarily-unavailable';
      if (versionStatus === 'available') hasSuccessfulVersionHeartbeat.current = true;
      if (versionStatus !== 'deferred') {
        setRecoveryActive(versionStatus === 'temporarily-unavailable' || versionStatus === 'unreachable');
      }

      return {
        banner,
        uiVersionStatus: maintenanceStarted
          ? await refreshUiForMaintenanceStart({ appBaseUrl, signal })
          : versionStatus,
      };
    },
    refetchInterval,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    retry: false,
  });
}

async function getBanner(lang: string): Promise<BannerDto | null> {
  try {
    const res = await get(`Banner?lang=${lang}`, false, true);
    if (res?.status === 204) return null;
    return parseBanner(res?.data) ?? null;
  } catch (err) {
    console.warn('Banner request failed', err);
    return null;
  }
}
